import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FraudWarning, FraudDisclaimer } from '../components/FraudDisclaimer';
import { Fingerprint, Camera, CircleDollarSign, CheckCircle, AlertTriangle, Upload, X, ArrowRight, ArrowLeft, Loader2, Crown, Sparkles, Lightbulb, Video, Film } from 'lucide-react';

const MAKES = ['Toyota', 'Honda', 'Nissan', 'Hyundai', 'Kia', 'Mercedes-Benz', 'BMW', 'Volkswagen', 'Ford', 'Chevrolet', 'Genesis', 'SsangYong', 'Mazda', 'Subaru', 'Mitsubishi', 'Suzuki', 'Peugeot', 'Renault', 'Volvo', 'Audi', 'Porsche', 'Land Rover', 'Jaguar', 'Lexus', 'Acura', 'Tata', 'Mahindra', 'BYD', 'Geely', 'Great Wall', 'Chery', 'MG', 'Skoda', 'Dacia', 'Tesla'];
const MODELS = {
  Toyota: ['Corolla', 'Camry', 'RAV4', 'Hilux', 'Land Cruiser', 'Yaris', 'Prius', 'Fortuner', 'Highlander', 'Venza', 'C-HR', 'Supra'],
  Honda: ['Civic', 'Accord', 'CR-V', 'Fit', 'HR-V', 'Pilot', 'Odyssey', 'City'],
  Nissan: ['Altima', 'Sentra', 'Rogue', 'Pathfinder', 'Patrol', 'X-Trail', 'Qashqai', 'Kicks', 'Navara'],
  Hyundai: ['Elantra', 'Tucson', 'Sonata', 'Santa Fe', 'Accent', 'Creta', 'Kona', 'Palisade', 'Venue', 'i10', 'i20', 'i30'],
  Kia: ['Forte', 'Sportage', 'Optima', 'Sorento', 'Rio', 'Seltos', 'Telluride', 'Carnival', 'K5', 'EV6', 'Niro', 'Picanto'],
  'Mercedes-Benz': ['C-Class', 'E-Class', 'GLC', 'GLE', 'S-Class', 'A-Class', 'GLA', 'GLB', 'GLS', 'CLA'],
  BMW: ['3 Series', '5 Series', 'X3', 'X5', '7 Series', 'X1', 'X4', 'X6', '1 Series', '2 Series', '4 Series', 'iX'],
  Volkswagen: ['Golf', 'Tiguan', 'Passat', 'Polo', 'T-Roc', 'Touareg', 'Atlas', 'ID.4', 'Jetta'],
  Ford: ['Focus', 'Escape', 'Explorer', 'Ranger', 'F-150', 'Bronco', 'EcoSport', 'Edge', 'Mustang'],
  Chevrolet: ['Malibu', 'Equinox', 'Silverado', 'Cruze', 'Trax', 'Blazer', 'Tahoe', 'Traverse'],
  Genesis: ['G70', 'G80', 'G90', 'GV70', 'GV80', 'GV60'],
  SsangYong: ['Tivoli', 'Korando', 'Rexton', 'Musso', 'Torres'],
  Mazda: ['Mazda3', 'Mazda6', 'CX-5', 'CX-30', 'CX-9', 'MX-5'],
  Subaru: ['Impreza', 'Outback', 'Forester', 'Crosstrek', 'WRX', 'Legacy'],
  Mitsubishi: ['Outlander', 'Eclipse Cross', 'ASX', 'Pajero', 'L200', 'Triton'],
  Suzuki: ['Swift', 'Vitara', 'Jimny', 'S-Cross', 'Baleno', 'Alto'],
  Peugeot: ['208', '308', '3008', '5008', '2008', '508'],
  Renault: ['Clio', 'Duster', 'Captur', 'Koleos', 'Megane', 'Kadjar'],
  Volvo: ['XC40', 'XC60', 'XC90', 'S60', 'S90', 'V60'],
  Audi: ['A3', 'A4', 'A6', 'Q3', 'Q5', 'Q7', 'e-tron', 'A5'],
  Porsche: ['Cayenne', 'Macan', 'Panamera', '911', 'Taycan'],
  'Land Rover': ['Range Rover', 'Discovery', 'Defender', 'Evoque', 'Velar'],
  Jaguar: ['F-Pace', 'E-Pace', 'XE', 'XF', 'F-Type'],
  Lexus: ['RX', 'NX', 'ES', 'IS', 'GX', 'LX', 'UX'],
  Acura: ['MDX', 'RDX', 'TLX', 'Integra'],
  Tata: ['Nexon', 'Harrier', 'Safari', 'Punch', 'Tiago'],
  Mahindra: ['XUV700', 'Thar', 'Scorpio', 'Bolero', 'XUV300'],
  BYD: ['Atto 3', 'Han', 'Tang', 'Dolphin', 'Seal', 'Song Plus'],
  Geely: ['Coolray', 'Azkarra', 'Okavango', 'Emgrand'],
  'Great Wall': ['Haval H6', 'Haval Jolion', 'Haval H2', 'Poer', 'Wingle'],
  Chery: ['Tiggo 7', 'Tiggo 8', 'Tiggo 4', 'Arrizo 6'],
  MG: ['ZS', 'HS', 'MG5', 'MG4', 'Gloster'],
  Skoda: ['Octavia', 'Kodiaq', 'Karoq', 'Superb', 'Fabia', 'Kamiq'],
  Dacia: ['Duster', 'Sandero', 'Logan', 'Spring', 'Jogger'],
  Tesla: ['Model 3', 'Model Y', 'Model S', 'Model X'],
};
const LOCATIONS = ['Accra', 'Kumasi', 'Tamale', 'Cape Coast', 'Takoradi', 'Tema', 'Ho', 'Sunyani', 'Bolgatanga', 'Koforidua'];

export default function CreateListing() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [vinResult, setVinResult] = useState(null);
  const [priceResult, setPriceResult] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [analyzingImage, setAnalyzingImage] = useState(null);
  const [showVinWarning, setShowVinWarning] = useState(false);
  const [form, setForm] = useState({
    vin: '', title: '', make: '', model: '', year: 2020,
    price: '', description: '', mileage: '', transmission: 'Automatic',
    fuel_type: 'Petrol', condition: 'Used', location: 'Accra', color: '',
  });

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  // Check listing eligibility on mount
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const res = await API.get('/listings/eligibility');
        setEligibility(res.data);
      } catch (error) {
        console.error('Error checking eligibility:', error);
        setEligibility({ can_create_listing: true }); // Fail open
      } finally {
        setCheckingEligibility(false);
      }
    };
    checkEligibility();
  }, []);

  // Show upgrade prompt if user has reached listing limit
  if (checkingEligibility) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </main>
    );
  }

  if (eligibility && !eligibility.can_create_listing) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-card border border-border/50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-8 h-8 text-gold" />
            </div>
            <h1 className="text-2xl font-bold mb-3">Listing Limit Reached</h1>
            <p className="text-muted-foreground mb-6">
              You've used all {eligibility.total_slots} of your listing slots. 
              ({eligibility.active_listings} active listing{eligibility.active_listings > 1 ? 's' : ''})
            </p>
            
            {/* Buy Slots Option */}
            <div className="space-y-4 mb-6">
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 text-left">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gold">Buy More Slots</h3>
                    <p className="text-sm text-muted-foreground">GHS 10 per slot, never expires</p>
                  </div>
                  <Button 
                    onClick={() => navigate('/buy-slots')} 
                    className="bg-gold text-black hover:brightness-110"
                    data-testid="buy-slots-btn"
                  >
                    Buy Slots
                  </Button>
                </div>
              </div>
            </div>

            {/* Dealer Option */}
            <div className="border-t border-border/50 pt-6">
              <p className="text-sm text-muted-foreground mb-4">Or get unlimited listings</p>
              <Button 
                onClick={() => navigate(eligibility.kyc_verified ? '/dealer' : '/kyc')} 
                variant="outline"
                className="w-full h-12 border-gold/50 text-gold hover:bg-gold/10 gap-2"
                data-testid="become-dealer-btn"
              >
                <Crown className="w-4 h-4" />
                {eligibility.kyc_verified ? 'Become a Dealer - From GHS 50/mo' : 'Verify Identity to Become Dealer'}
              </Button>
            </div>
            
            <button 
              onClick={() => navigate('/dashboard')} 
              className="mt-6 text-sm text-muted-foreground hover:text-foreground"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    );
  }

  const decodeVin = async () => {
    if (form.vin.length !== 17) { toast.error('VIN must be 17 characters'); return; }
    try {
      const res = await API.post('/vin/decode', { vin: form.vin });
      setVinResult(res.data);
      if (res.data.valid && res.data.make !== 'Unknown') {
        update('make', res.data.make);
        if (res.data.year) update('year', res.data.year);
        toast.success(`VIN decoded: ${res.data.make} (${res.data.origin})`);
      } else if (res.data.valid) {
        toast.info('VIN valid but manufacturer not in our database');
      } else {
        toast.error(res.data.errors?.join(', ') || 'Invalid VIN');
      }
    } catch { toast.error('VIN decode failed'); }
  };

  const validatePrice = async () => {
    if (!form.price || !form.make || !form.model) { toast.error('Enter make, model and price first'); return; }
    try {
      const res = await API.post('/pricing/validate', {
        make: form.make, model: form.model, year: form.year, price: parseFloat(form.price),
      });
      setPriceResult(res.data);
    } catch { toast.error('Price validation failed'); }
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 8) { toast.error('Maximum 8 photos'); return; }
    setUploading(true);

    // Soft AI Assistant: collect issues silently; do not block uploads
    let okCount = 0;
    let warnCount = 0;
    let failedCount = 0;

    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      setAnalyzingImage(file.name);

      try {
        // Use upload with AI analysis
        const res = await API.post('/upload/with-analysis', fd);
        const analysis = res.data.analysis || {};
        const issues = analysis.issues || {};

        const tips = [];
        if (issues.contact_info_detected) tips.push('Contact info visible');
        if (issues.license_plate_edited) tips.push('Plate looks edited/blurred');
        if (issues.low_quality) tips.push('Image is a bit blurry');
        if (analysis.quality_score !== undefined && analysis.quality_score < 50) tips.push('Low quality score');
        if (!analysis.is_acceptable && analysis.rejection_reason) tips.push(analysis.rejection_reason);

        const photoData = {
          url: res.data.url,
          suspicious: res.data.suspicious,
          analysis,
          tips,
          fileName: file.name,
        };

        if (tips.length === 0 && !res.data.suspicious) okCount += 1;
        else warnCount += 1;

        // Always add the photo - user decides. No blocking.
        setPhotos(p => [...p, photoData]);
      } catch (err) {
        failedCount += 1;
      }
    }

    setAnalyzingImage(null);
    setUploading(false);

    // One batched, friendly summary instead of multiple toasts
    if (failedCount > 0) {
      toast.error(`${failedCount} photo${failedCount > 1 ? 's' : ''} failed to upload. Please try again.`);
    }
    if (okCount > 0 && warnCount === 0) {
      toast.success(`${okCount} photo${okCount > 1 ? 's' : ''} look great`);
    } else if (okCount > 0 && warnCount > 0) {
      toast(`${okCount} look great, ${warnCount} could be clearer. See AI tips below.`, { icon: '✨' });
    } else if (okCount === 0 && warnCount > 0) {
      toast(`Uploaded ${warnCount} photo${warnCount > 1 ? 's' : ''}. See AI tips below.`, { icon: '💡' });
    }
  };

  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx));

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (videos.length >= 1) {
      toast.error('Only one video allowed per listing');
      return;
    }
    // 100MB hard cap pre-compression
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video too large (max 100MB)');
      return;
    }
    setUploadingVideo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await API.post('/upload/video', fd, { timeout: 180000 });
      setVideos([{ url: res.data.url, thumbnail_url: res.data.thumbnail_url }]);
      toast.success('Video uploaded — compressed and ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Video upload failed');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const removeVideo = () => setVideos([]);

  const handlePublishClick = () => {
    // Validate required fields first
    if (!form.title) {
      toast.error('Please enter a listing title');
      return;
    }
    if (!form.make) {
      toast.error('Please select a car make');
      return;
    }
    if (!form.model) {
      toast.error('Please select a car model');
      return;
    }
    if (!form.price || parseFloat(form.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    // Show VIN warning dialog if no VIN
    if (!form.vin) {
      setShowVinWarning(true);
      return;
    }
    
    // Proceed directly if VIN is provided
    submitListing();
  };

  const submitListing = async () => {
    setShowVinWarning(false);
    setLoading(true);
    try {
      const payload = { 
        ...form, 
        vin: form.vin || null,  // Send null if empty
        price: parseFloat(form.price), 
        year: parseInt(form.year) || new Date().getFullYear(),
        mileage: form.mileage ? parseInt(form.mileage) : null, 
        photos: photos.map(p => p.url),
        videos: videos,
      };
      
      console.log('Submitting listing:', payload);
      const res = await API.post('/listings', payload);
      console.log('Listing created:', res.data);
      
      toast.success('Listing created successfully!');
      navigate(`/listing/${res.data.listing_id}`);
    } catch (err) {
      console.error('Failed to create listing:', err);
      
      // Extract error message
      let errorMsg = 'Failed to create listing';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (err.response.data.detail.message) {
          errorMsg = err.response.data.detail.message;
        } else if (Array.isArray(err.response.data.detail)) {
          // Pydantic validation errors
          errorMsg = err.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <main data-testid="create-listing-page" className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <h1 className="text-2xl font-bold mb-2">Sell Your Car</h1>
        <p className="text-sm text-muted-foreground mb-4">Complete the steps below to create a verified listing.</p>
        
        {/* Fraud Warning */}
        <FraudWarning type="create" />
        
        <div className="mt-8">
          {/* Step indicators */}
          <div className="flex gap-1 mb-10">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${s <= step ? 'bg-gold' : 'bg-muted'}`} />
            ))}
          </div>

        {/* Step 1: VIN & Details */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-1">
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center"><Fingerprint className="w-4 h-4 text-gold" /></div>
                <h2 className="font-semibold">VIN & Vehicle Info</h2>
              </div>
              <div className="space-y-2">
                <Label>VIN Number <span className="text-muted-foreground text-xs">(Optional but recommended)</span></Label>
                <div className="flex gap-2">
                  <Input placeholder="Enter 17-character VIN" value={form.vin} onChange={(e) => update('vin', e.target.value.toUpperCase())}
                    className="font-mono-display h-12" maxLength={17} data-testid="vin-input" />
                  <Button onClick={decodeVin} variant="outline" className="h-12 px-6" data-testid="decode-vin-btn" disabled={!form.vin || form.vin.length < 17}>Decode</Button>
                </div>
                {!form.vin && (
                  <div className="flex items-center gap-2 text-sm mt-2 text-yellow-500 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>Without VIN, your trust score will be capped at 40. Add VIN to increase buyer confidence!</span>
                  </div>
                )}
                {vinResult && (
                  <div className={`flex items-center gap-2 text-sm mt-2 ${vinResult.valid ? 'text-trust-green' : 'text-alert-red'}`}>
                    {vinResult.valid ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    {vinResult.valid ? `${vinResult.make} from ${vinResult.origin}` : vinResult.errors?.join(', ')}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Listing Title *</Label>
                <Input placeholder="e.g. 2019 Toyota Camry - Well Maintained" value={form.title} onChange={(e) => update('title', e.target.value)}
                  className="h-12" data-testid="title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Make *</Label>
                  <Select value={form.make} onValueChange={(v) => { update('make', v); update('model', ''); }}>
                    <SelectTrigger className="h-12" data-testid="make-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{MAKES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Model *</Label>
                  <Select value={form.model} onValueChange={(v) => update('model', v)}>
                    <SelectTrigger className="h-12" data-testid="model-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{(MODELS[form.make] || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Year *</Label>
                  <Input type="number" min={2000} max={2026} value={form.year} onChange={(e) => update('year', e.target.value)}
                    className="h-12" data-testid="year-input" />
                </div>
                <div className="space-y-2">
                  <Label>Transmission</Label>
                  <Select value={form.transmission} onValueChange={(v) => update('transmission', v)}>
                    <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Automatic">Automatic</SelectItem>
                      <SelectItem value="Manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fuel Type</Label>
                  <Select value={form.fuel_type} onValueChange={(v) => update('fuel_type', v)}>
                    <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Petrol">Petrol</SelectItem>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="Hybrid">Hybrid</SelectItem>
                      <SelectItem value="Electric">Electric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Mileage (km)</Label>
                  <Input type="number" placeholder="e.g. 45000" value={form.mileage} onChange={(e) => update('mileage', e.target.value)}
                    className="h-12" data-testid="mileage-input" />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select value={form.location} onValueChange={(v) => update('location', v)}>
                    <SelectTrigger className="h-12" data-testid="location-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} className="bg-gold text-black hover:brightness-110 gap-2 h-12 px-8" data-testid="next-step-btn">
                Next: Photos <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Photos */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-2">
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center"><Camera className="w-4 h-4 text-gold" /></div>
                <h2 className="font-semibold">Photos & Description</h2>
              </div>
              
              {/* AI Quality Info */}
              <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40 rounded-lg p-3 flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Soft AI Assistant:</span> We'll scan your photos and offer tips. You're always in control — upload anything you'd like.
                </div>
              </div>
              
              <div>
                <label className={`flex flex-col items-center gap-3 border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-gold/50 transition-colors ${uploading ? 'pointer-events-none opacity-60' : ''}`} data-testid="photo-upload">
                  {uploading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-gold animate-spin" />
                      <span className="text-sm text-muted-foreground">
                        {analyzingImage ? `Analyzing ${analyzingImage}...` : 'Uploading...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Click to upload photos (max 8)</span>
                    </>
                  )}
                  <input type="file" accept="image/*" multiple onChange={uploadPhoto} className="hidden" disabled={uploading} />
                </label>
                {photos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {photos.map((p, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border/50 group">
                        <img src={`${BACKEND_URL}${p.url}`} alt="" className="w-full h-full object-cover" />

                        {/* Soft Tip Indicator (amber dot, no big red overlay) */}
                        {(p.tips?.length > 0 || p.suspicious) && (
                          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/90 text-white flex items-center gap-1" title={(p.tips || []).join(' · ')}>
                            <Lightbulb className="w-2.5 h-2.5" />
                            Tip
                          </div>
                        )}

                        <button onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Soft AI Assistant Panel - batched, advisory, non-blocking */}
                {photos.length > 0 && photos.some(p => p.tips?.length > 0 || p.suspicious) && (
                  <div className="mt-4 rounded-xl border border-amber-300/40 bg-amber-50 dark:bg-amber-950/20 p-4" data-testid="ai-assistant-panel">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                          <h4 className="text-sm font-semibold">AI Assistant Tips</h4>
                          <span className="text-xs text-muted-foreground">
                            {photos.filter(p => !(p.tips?.length > 0 || p.suspicious)).length} look great · {photos.filter(p => p.tips?.length > 0 || p.suspicious).length} could be clearer
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {photos.map((p, i) => (
                            (p.tips?.length > 0) && (
                              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                <span className="font-mono text-amber-600 dark:text-amber-400 flex-shrink-0">#{i + 1}</span>
                                <span>{p.tips.join(' · ')}</span>
                              </li>
                            )
                          ))}
                        </ul>
                        <p className="text-[11px] text-muted-foreground/80 mt-3">
                          These are suggestions, not blockers. You can proceed with any photos you've uploaded.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Video Upload */}
              <div className="space-y-3" data-testid="video-upload-section">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><Film className="w-4 h-4 text-gold" /> Video Walkaround <span className="text-xs text-muted-foreground font-normal">(optional, max 1)</span></Label>
                  {videos.length === 0 && !uploadingVideo && (
                    <span className="text-[11px] text-muted-foreground">Compressed automatically, trimmed to 60s</span>
                  )}
                </div>

                {videos.length === 0 ? (
                  <label className={`flex flex-col items-center gap-3 border-2 border-dashed border-border rounded-xl p-6 cursor-pointer hover:border-gold/50 transition-colors ${uploadingVideo ? 'pointer-events-none opacity-60' : ''}`} data-testid="video-upload-label">
                    {uploadingVideo ? (
                      <>
                        <Loader2 className="w-7 h-7 text-gold animate-spin" />
                        <span className="text-sm text-muted-foreground">Compressing your video… this can take up to 30 seconds.</span>
                      </>
                    ) : (
                      <>
                        <Video className="w-7 h-7 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Tap to upload a short video (MP4, MOV, WEBM)</span>
                      </>
                    )}
                    <input type="file" accept="video/*" onChange={uploadVideo} className="hidden" disabled={uploadingVideo} data-testid="video-upload-input" />
                  </label>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-border/50 bg-black aspect-video" data-testid="video-preview">
                    <video
                      src={`${BACKEND_URL}${videos[0].url}`}
                      poster={videos[0].thumbnail_url ? `${BACKEND_URL}${videos[0].thumbnail_url}` : undefined}
                      controls
                      preload="metadata"
                      className="w-full h-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={removeVideo}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center"
                      data-testid="video-remove-btn"
                      title="Remove video"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea placeholder="Describe your car - condition, history, features..." value={form.description}
                  onChange={(e) => update('description', e.target.value)} rows={5} data-testid="description-input" />
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Input placeholder="e.g. Silver" value={form.color} onChange={(e) => update('color', e.target.value)} className="h-12" />
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} className="gap-2 h-12"><ArrowLeft className="w-4 h-4" /> Back</Button>
              <Button onClick={() => setStep(3)} className="bg-gold text-black hover:brightness-110 gap-2 h-12 px-8" data-testid="next-step-2-btn">
                Next: Pricing <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-3">
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center"><CircleDollarSign className="w-4 h-4 text-gold" /></div>
                <h2 className="font-semibold">Pricing</h2>
              </div>
              <div className="space-y-2">
                <Label>Price (GHS) *</Label>
                <Input type="number" placeholder="e.g. 120000" value={form.price} onChange={(e) => update('price', e.target.value)}
                  className="font-mono-display h-12 text-lg" data-testid="price-input" />
                {/* AI Market Price Check temporarily disabled - awaiting reliable data sources */}
              </div>
              {/* Price validation result hidden until reliable market data available */}
              <div className="space-y-2">
                <Label>Condition</Label>
                <Select value={form.condition} onValueChange={(v) => update('condition', v)}>
                  <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">Brand New</SelectItem>
                    <SelectItem value="Used">Used</SelectItem>
                    <SelectItem value="Certified Pre-Owned">Certified Pre-Owned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} className="gap-2 h-12"><ArrowLeft className="w-4 h-4" /> Back</Button>
              <Button onClick={handlePublishClick} disabled={loading} className="bg-gold text-black hover:brightness-110 gap-2 h-12 px-8" data-testid="submit-listing-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    Publish Listing <CheckCircle className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
            
            {/* Fraud Disclaimer */}
            <FraudDisclaimer />
          </div>
        )}
        </div>
      </div>
      
      {/* VIN Warning Dialog */}
      <Dialog open={showVinWarning} onOpenChange={setShowVinWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Low Trust Score Warning
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              <p className="mb-3">
                Without a VIN (Vehicle Identification Number), your listing will have a <strong className="text-yellow-500">maximum trust score of 40</strong>.
              </p>
              <p className="text-muted-foreground">
                Listings with higher trust scores get more views and buyer interest. Consider adding a VIN to increase your chances of selling.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowVinWarning(false)} className="w-full sm:w-auto">
              Go Back & Add VIN
            </Button>
            <Button onClick={submitListing} className="bg-gold text-black w-full sm:w-auto" data-testid="confirm-publish-btn">
              Publish Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
